import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/ToastContainer";
import { courseService } from "../services/courseService";
import { tradeService } from "../services/tradeService";
import { instructorService } from "../services/instructorService";
import { uploadImage } from "../services/uploadService";
import { parseApiError, formatValidationErrors } from "../utils/errorHandler";
import {
  ArrowLeft,
  Upload,
  X,
  Plus,
  Trash2,
  Loader2,
  Save,
  Layers,
} from "lucide-react";

interface Trade {
  _id: string;
  name: string;
}

interface Instructor {
  _id: string;
  user?: {
    firstName: string;
    lastName: string;
  };
  professionalTitle?: string;
}

interface FormData {
  title: string;
  slug: string;
  description: string;
  trade: string;
  instructor: string;
  level: "beginner" | "intermediate" | "advanced";
  language: "Arabic" | "English" | "Both";
  price: number;
  discountPrice: number;
  isFree: boolean;
  maxFreeLessons: number;
  thumbnail: string;
  thumbnailVideoUrl: string;
  previewVideo: string;
  learningOutcomes: string[];
  requirements: string[];
  targetAudience: string[];
  tags: string;
  certificateAvailable: boolean;
  isTrending: boolean;
  duration: number;
  accessDurationDays: number | null;
}

// Helper components
function SectionHeader({ number, title }: { number: number; title: string }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <div className="w-8 h-8 rounded-full bg-[#e07828] flex items-center justify-center text-white font-bold text-sm">
        {number}
      </div>
      <h3 className="text-[16px] font-semibold text-[#102040]">{title}</h3>
    </div>
  );
}

function FormField({
  label,
  required,
  children,
  hint,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div>
      <label className="text-[14px] font-normal text-[#102040] tracking-[0.5px] mb-2 block">
        {label} {required && <span className="text-[#FF0000]">*</span>}
      </label>
      {children}
      {hint && <p className="text-[12px] text-[#706F6F] mt-1">{hint}</p>}
    </div>
  );
}

export default function AddEditCoursePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [isFetching, setIsFetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const [trades, setTrades] = useState<Trade[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const { showSuccess, showError, showWarning } = useToast();
  const [mediaTab, setMediaTab] = useState<"image" | "video">("image");

  const [formData, setFormData] = useState<FormData>({
    title: "",
    slug: "",
    description: "",
    trade: "",
    instructor: "",
    level: "beginner",
    language: "Arabic",
    price: 0,
    discountPrice: 0,
    isFree: false,
    maxFreeLessons: 0,
    thumbnail: "",
    thumbnailVideoUrl: "",
    previewVideo: "",
    learningOutcomes: [""],
    requirements: [""],
    targetAudience: [""],
    tags: "",
    certificateAvailable: false,
    isTrending: false,
    duration: 0,
    accessDurationDays: null,
  });

  const inputClassName =
    "w-full px-4 py-3 bg-white border border-[#DCE0E5] rounded-[8px] text-[14px] text-[#102040] placeholder:text-[#706F6F] focus:outline-none focus:ring-2 focus:ring-[#e07828]/20 focus:border-[#e07828] transition-all";

  const selectClassName =
    "w-full px-4 py-3 bg-white border border-[#DCE0E5] rounded-[8px] text-[14px] text-[#102040] focus:outline-none focus:ring-2 focus:ring-[#e07828]/20 focus:border-[#e07828] transition-all appearance-none cursor-pointer";

  // Fetch trades and instructors for dropdowns
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [tradesRes, instructorsRes] = await Promise.all([
          tradeService.getAllTrades({ isActive: true }),
          instructorService.getAllInstructors({ limit: 100 }),
        ]);
        setTrades(tradesRes.trades || []);
        setInstructors(instructorsRes.instructors || []);
      } catch (err) {
        console.error("Failed to fetch dropdown data:", err);
        showWarning(
          "Some dropdown data failed to load. You may need to refresh the page.",
          "Warning"
        );
      }
    };
    fetchDropdownData();
  }, []);

  // Fetch course data if editing
  const fetchCourse = useCallback(async () => {
    if (!id) return;
    setIsFetching(true);
    try {
      const response = await courseService.getCourseById(id);
      const course = response.course;
      setFormData({
        title: course.title || "",
        slug: course.slug || "",
        description: course.description || "",
        trade: (course.trade as any)?._id || (course.trade as string) || "",
        instructor:
          (course.instructor as any)?._id ||
          (course.instructor as string) ||
          "",
        level: (course.level as any) || "beginner",
        language: (course.language as any) || "Arabic",
        price: course.price || 0,
        discountPrice: course.discountPrice || 0,
        isFree: course.isFree || false,
        maxFreeLessons: course.maxFreeLessons || 0,
        thumbnail: course.thumbnail || "",
        thumbnailVideoUrl: course.thumbnailVideoUrl || "",
        previewVideo: course.previewVideo || "",
        learningOutcomes:
          course.learningOutcomes?.length > 0 ? course.learningOutcomes : [""],
        requirements:
          course.requirements?.length > 0 ? course.requirements : [""],
        targetAudience:
          course.targetAudience?.length > 0 ? course.targetAudience : [""],
        tags: course.tags?.join(", ") || "",
        certificateAvailable: course.certificateAvailable || false,
        isTrending: course.isTrending || false,
        duration: course.duration || 0,
        accessDurationDays: course.accessDurationDays ?? null,
      });

      if (course.thumbnailVideoUrl) {
        setMediaTab("video");
      }
    } catch (err: any) {
      console.error("Failed to fetch course:", err);
      const apiError = parseApiError(err);
      setError(apiError.message);
      showError(apiError.message, "Failed to Load Course");
    } finally {
      setIsFetching(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEditMode) {
      fetchCourse();
    }
  }, [isEditMode, fetchCourse]);

  // Auto-generate slug from title
  useEffect(() => {
    if (!isEditMode && formData.title) {
      const slug = formData.title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
      setFormData((prev) => ({ ...prev, slug }));
    }
  }, [formData.title, isEditMode]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleArrayChange = (
    field: "learningOutcomes" | "requirements" | "targetAudience",
    index: number,
    value: string
  ) => {
    setFormData((prev) => {
      const newArray = [...prev[field]];
      newArray[index] = value;
      return { ...prev, [field]: newArray };
    });
  };

  const handleAddArrayItem = (
    field: "learningOutcomes" | "requirements" | "targetAudience"
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], ""],
    }));
  };

  const handleRemoveArrayItem = (
    field: "learningOutcomes" | "requirements" | "targetAudience",
    index: number
  ) => {
    setFormData((prev) => {
      const newArray = prev[field].filter((_, i) => i !== index);
      return {
        ...prev,
        [field]: newArray.length > 0 ? newArray : [""],
      };
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const url = await uploadImage(file, "courses");
      setFormData((prev) => ({ ...prev, thumbnail: url }));
    } catch (err) {
      console.error("Failed to upload image:", err);
      const apiError = parseApiError(err);
      setError(apiError.message);
      showError(apiError.message, "Image Upload Failed");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setValidationErrors({});

    try {
      // Clean up arrays (remove empty strings)
      const learningOutcomes = formData.learningOutcomes.filter(
        (item) => item.trim() !== ""
      );
      const requirements = formData.requirements.filter(
        (item) => item.trim() !== ""
      );
      const targetAudience = formData.targetAudience.filter(
        (item) => item.trim() !== ""
      );
      const tags = formData.tags
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t !== "");

      const payload = {
        title: formData.title,
        slug: formData.slug,
        description: formData.description,
        trade: formData.trade,
        instructor: formData.instructor || undefined,
        level: formData.level,
        language: formData.language,
        price: formData.isFree ? 0 : Number(formData.price),
        discountPrice: formData.isFree ? 0 : Number(formData.discountPrice),
        isFree: formData.isFree,
        maxFreeLessons: Number(formData.maxFreeLessons),
        thumbnail: formData.thumbnail || undefined,
        thumbnailVideoUrl: formData.thumbnailVideoUrl || undefined,
        previewVideo: formData.previewVideo || undefined,
        learningOutcomes,
        requirements,
        targetAudience,
        tags,
        certificateAvailable: formData.certificateAvailable,
        isTrending: formData.isTrending,
        duration: Number(formData.duration) || undefined,
        accessDurationDays: formData.accessDurationDays ?? undefined,
      };

      if (isEditMode && id) {
        await courseService.updateCourse(id, payload);
        showSuccess("Course updated successfully", "Success");
      } else {
        await courseService.createCourse(payload);
        showSuccess("Course created successfully", "Success");
      }

      navigate("/courses");
    } catch (err: any) {
      console.error("Failed to save course:", err);
      const apiError = parseApiError(err);

      if (apiError.errors && Object.keys(apiError.errors).length > 0) {
        setValidationErrors(apiError.errors);
        setError("Please fix the validation errors below.");
      } else {
        setError(apiError.message);
      }

      showError(
        apiError.message,
        isEditMode ? "Update Failed" : "Create Failed"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isFetching) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#e07828]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <form onSubmit={handleSubmit} className="w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/courses"
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-[#102040]">
                {isEditMode ? "Edit Course" : "Add New Course"}
              </h1>
              <p className="text-[#706F6F] text-sm">
                {isEditMode
                  ? "Update course details and settings"
                  : "Fill in the details to create a new course"}
              </p>
            </div>
          </div>
          {isEditMode && id && (
            <Link
              to={`/courses/${id}/structure`}
              className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
            >
              <Layers size={18} />
              Manage Structure
            </Link>
          )}
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 font-medium">{error}</p>
            {Object.keys(validationErrors).length > 0 && (
              <ul className="mt-2 ml-4 list-disc space-y-1 text-sm text-red-600">
                {formatValidationErrors(validationErrors).map((err, idx) => (
                  <li key={idx}>
                    <span className="font-medium">{err.field}:</span>{" "}
                    {err.messages.join(", ")}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="space-y-6">
          {/* Section 1: Basic Information */}
          <Card className="p-4 sm:p-6">
            <SectionHeader number={1} title="Basic Information" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
              <div className="md:col-span-2">
                <FormField label="Course Title" required>
                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Enter course title"
                    required
                    className={inputClassName}
                  />
                </FormField>
              </div>

              <FormField label="Slug" required hint="URL-friendly identifier">
                <Input
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  placeholder="course-slug"
                  required
                  className={inputClassName}
                />
              </FormField>

              <FormField label="Trade / Category" required>
                <select
                  name="trade"
                  value={formData.trade}
                  onChange={handleChange}
                  required
                  className={selectClassName}
                >
                  <option value="">Select Trade</option>
                  {trades.map((trade) => (
                    <option key={trade._id} value={trade._id}>
                      {trade.name}
                    </option>
                  ))}
                </select>
              </FormField>

              <FormField label="Instructor">
                <select
                  name="instructor"
                  value={formData.instructor}
                  onChange={handleChange}
                  className={selectClassName}
                >
                  <option value="">Select Instructor (Optional)</option>
                  {instructors.map((instructor) => (
                    <option key={instructor._id} value={instructor._id}>
                      {instructor.user?.firstName} {instructor.user?.lastName}
                    </option>
                  ))}
                </select>
              </FormField>

              <FormField label="Level" required>
                <select
                  name="level"
                  value={formData.level}
                  onChange={handleChange}
                  required
                  className={selectClassName}
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </FormField>

              <FormField label="Language" required>
                <select
                  name="language"
                  value={formData.language}
                  onChange={handleChange}
                  required
                  className={selectClassName}
                >
                  <option value="Arabic">Arabic</option>
                  <option value="English">English</option>
                  <option value="Both">Both</option>
                </select>
              </FormField>

              <div className="md:col-span-2">
                <FormField label="Description" required>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    required
                    className={inputClassName + " resize-none"}
                    placeholder="Describe your course..."
                  />
                </FormField>
              </div>
            </div>
          </Card>

          {/* Section 2: Pricing */}
          <Card className="p-4 sm:p-6">
            <SectionHeader number={2} title="Pricing" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="isFree"
                  name="isFree"
                  checked={formData.isFree}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-gray-300 text-[#e07828] focus:ring-[#e07828]"
                />
                <label htmlFor="isFree" className="text-[14px] text-[#102040]">
                  This is a free course
                </label>
              </div>

              <FormField label="Price ($)">
                <Input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="0.00"
                  min={0}
                  step={0.01}
                  disabled={formData.isFree}
                  className={inputClassName}
                />
              </FormField>

              <FormField label="Discount Price ($)">
                <Input
                  type="number"
                  name="discountPrice"
                  value={formData.discountPrice}
                  onChange={handleChange}
                  placeholder="0.00"
                  min={0}
                  step={0.01}
                  disabled={formData.isFree}
                  className={inputClassName}
                />
              </FormField>

              <FormField
                label="Max Free Lessons"
                hint="Number of lessons available for preview"
              >
                <Input
                  type="number"
                  name="maxFreeLessons"
                  value={formData.maxFreeLessons}
                  onChange={handleChange}
                  placeholder="0"
                  min={0}
                  className={inputClassName}
                />
              </FormField>

              <FormField
                label="Course Duration (Hours)"
                hint="Total hours of course content (used for certificates)"
              >
                <Input
                  type="number"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  placeholder="0"
                  min={0}
                  step={1}
                  className={inputClassName}
                />
              </FormField>

              <FormField
                label="Enrollment Duration (Days)"
                hint="Number of days students have access after enrollment. Leave empty for lifetime access."
              >
                <Input
                  type="number"
                  name="accessDurationDays"
                  value={formData.accessDurationDays ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      accessDurationDays: value === "" ? null : parseInt(value),
                    }));
                  }}
                  placeholder="Lifetime access"
                  min={1}
                  className={inputClassName}
                />
              </FormField>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="certificateAvailable"
                  name="certificateAvailable"
                  checked={formData.certificateAvailable}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-gray-300 text-[#e07828] focus:ring-[#e07828]"
                />
                <label
                  htmlFor="certificateAvailable"
                  className="text-[14px] text-[#102040]"
                >
                  Certificate Available
                </label>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="isTrending"
                  name="isTrending"
                  checked={formData.isTrending}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-gray-300 text-[#e07828] focus:ring-[#e07828]"
                />
                <label
                  htmlFor="isTrending"
                  className="text-[14px] text-[#102040]"
                >
                  Is Trending
                </label>
              </div>
            </div>
          </Card>

          {/* Section 3: Media */}
          <Card className="p-4 sm:p-6">
            <SectionHeader number={3} title="Media" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
              <div className="md:col-span-2">
                <FormField label="Thumbnail Type" required>
                  <div className="flex gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => setMediaTab("image")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        mediaTab === "image"
                          ? "bg-[#e07828] text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      Image Upload
                    </button>
                    <button
                      type="button"
                      onClick={() => setMediaTab("video")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        mediaTab === "video"
                          ? "bg-[#e07828] text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      YouTube Video
                    </button>
                  </div>
                </FormField>

                {mediaTab === "image" ? (
                  <FormField label="Thumbnail Image" required>
                    <div className="flex items-start gap-4">
                      {formData.thumbnail ? (
                        <div className="relative">
                          <img
                            src={formData.thumbnail}
                            alt="Thumbnail"
                            className="w-32 h-24 object-cover rounded-lg border"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                thumbnail: "",
                              }))
                            }
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-32 h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#e07828] transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                            disabled={isUploading}
                          />
                          {isUploading ? (
                            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                          ) : (
                            <>
                              <Upload
                                size={20}
                                className="text-gray-400 mb-1"
                              />
                              <span className="text-xs text-gray-500">
                                Upload
                              </span>
                            </>
                          )}
                        </label>
                      )}
                    </div>
                  </FormField>
                ) : (
                  <FormField
                    label="YouTube Thumbnail URL"
                    required
                    hint="Enter a YouTube URL to use as thumbnail"
                  >
                    <Input
                      type="url"
                      name="thumbnailVideoUrl"
                      value={formData.thumbnailVideoUrl}
                      onChange={handleChange}
                      placeholder="https://youtube.com/watch?v=..."
                      className={inputClassName}
                    />
                  </FormField>
                )}
              </div>

              <div className="md:col-span-2">
                <FormField
                  label="Preview Video URL"
                  hint="YouTube or Vimeo URL"
                >
                  <Input
                    type="url"
                    name="previewVideo"
                    value={formData.previewVideo}
                    onChange={handleChange}
                    placeholder="https://youtube.com/watch?v=..."
                    className={inputClassName}
                  />
                </FormField>
              </div>
            </div>
          </Card>

          {/* Section 4: Learning Outcomes */}
          <Card className="p-4 sm:p-6">
            <SectionHeader number={4} title="Learning Outcomes" />
            <p className="text-[12px] text-[#706F6F] mb-4">
              What will students learn from this course?
            </p>
            <div className="space-y-3">
              {formData.learningOutcomes.map((outcome, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={outcome}
                    onChange={(e) =>
                      handleArrayChange(
                        "learningOutcomes",
                        index,
                        e.target.value
                      )
                    }
                    placeholder={`Outcome ${index + 1}`}
                    className={inputClassName + " flex-1"}
                  />
                  {formData.learningOutcomes.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoveArrayItem("learningOutcomes", index)
                      }
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => handleAddArrayItem("learningOutcomes")}
                className="flex items-center gap-2 text-[#e07828] hover:text-[#b39200] text-sm font-medium"
              >
                <Plus size={16} /> Add Learning Outcome
              </button>
            </div>
          </Card>

          {/* Section 5: Requirements */}
          <Card className="p-4 sm:p-6">
            <SectionHeader number={5} title="Requirements" />
            <p className="text-[12px] text-[#706F6F] mb-4">
              What knowledge should students have before taking this course?
            </p>
            <div className="space-y-3">
              {formData.requirements.map((req, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={req}
                    onChange={(e) =>
                      handleArrayChange("requirements", index, e.target.value)
                    }
                    placeholder={`Requirement ${index + 1}`}
                    className={inputClassName + " flex-1"}
                  />
                  {formData.requirements.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoveArrayItem("requirements", index)
                      }
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => handleAddArrayItem("requirements")}
                className="flex items-center gap-2 text-[#e07828] hover:text-[#b39200] text-sm font-medium"
              >
                <Plus size={16} /> Add Requirement
              </button>
            </div>
          </Card>

          {/* Section 6: Tags */}
          <Card className="p-4 sm:p-6">
            <SectionHeader number={6} title="Tags & SEO" />
            <FormField
              label="Tags"
              hint="Comma-separated tags for search and discovery"
            >
              <Input
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="javascript, web development, react"
                className={inputClassName}
              />
            </FormField>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-end gap-4 pt-2 pb-8">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/courses")}
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isEditMode ? "Update Course" : "Create Course"}
            </Button>
          </div>
        </div>
      </form>
    </DashboardLayout>
  );
}
